import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { navigateToAssessmentOrDashboard } from "../utils/assessmentNavigation";
import { Helmet } from "react-helmet";
import { Dialog } from "@headlessui/react";
import {
  CheckCircle2,
  FileText,
  User,
  Shield,
  Lock,
  FileCheck,
  Database,
  Info,
  X,
} from "lucide-react";
// Removed: import { BarChart3 } from "lucide-react";

import api from "../api";

// Icon map matching backend choices (case-insensitive)
const iconMap = {
  shield: Shield,
  lock: Lock,
  file: FileText,
  database: Database,
  user: User,
  report: FileCheck,
  reports: FileCheck,
  info: Info,
};

// Loading screen component
const LoadingScreen = ({ title = "Loading...", subtitle = "" }) => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 p-4">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
      <h2 className="text-xl font-semibold mb-2">{title}</h2>
      {subtitle && <p className="text-gray-600">{subtitle}</p>}
    </div>
  </div>
);

const PrivacyAssessmentNotice = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // All hooks are now at the top
  const framework = location.state?.framework;

  const [notice, setNotice] = useState(null);
  const [hasNotice, setHasNotice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acceptance, setAcceptance] = useState({
    acknowledgment1: false,
    acknowledgment2: false,
    acknowledgment3: false,
  });
  const [loadingState, setLoadingState] = useState("idle"); // 'idle' | 'accepting' | 'loadingDashboard'
  const [showCancelModal, setShowCancelModal] = useState(false);

  // React effect for redirect if no framework
  useEffect(() => {
    if (!framework) {
      navigate("/frameworks", { replace: true });
    }
  }, [framework, navigate]);

    // Fetch privacy notice
    useEffect(() => {
    if (!framework) {
        return;
    }

    const loadNotice = async () => {
        try {
        const res = await api.get("maturity/privacy/current/");

        if (res.data?.has_notice) {
            setHasNotice(true);
            setNotice(res.data.notice);
        } else {
            setHasNotice(false);
        }
        } catch (err) {
        setError(
            "Unable to load the current Privacy Notice. Please contact your system administrator or try again later."
        );
        } finally {
        setLoading(false);
        }
    };

    loadNotice();
    }, [framework]);

  const handleCheckboxChange = (key) => {
    setAcceptance((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAccept = async () => {
    if (
      Object.values(acceptance).some((v) => v === false) ||
      loadingState !== "idle"
    )
      return;
    setLoadingState("accepting");
    try {
      await api.post("maturity/privacy/accept/");
      await navigateToAssessmentOrDashboard(framework, navigate);
    } catch (err) {
      setError("Error during acceptance. Please contact your administrator.");
    } finally {
      setLoadingState("idle");
    }
  };

  const handleCancel = () => setShowCancelModal(true);
  const handleCloseModal = () => setShowCancelModal(false);
  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate("/", { replace: true });
  };

  if (loading || loadingState === "loadingDashboard") {
    return (
      <LoadingScreen
        title={loadingState === "loadingDashboard" ? "Preparing assessment..." : "Loading..."}
        subtitle={loadingState === "loadingDashboard" ? "Loading your assessment..." : ""}
      />
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-2xl mx-auto flex flex-col items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 text-red-600">
        <p className="mb-4">{error}</p>
        <button
          className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!hasNotice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 px-4">
        <div className="max-w-xl text-center bg-white p-8 rounded shadow">
          <h2 className="text-2xl font-bold mb-4">No active Privacy & Assessment Notice</h2>
          <p className="mb-4">
            There is no active Privacy & Assessment Notice published at this time.
            Please contact your administrator.
          </p>
          <button
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition"
            onClick={() => navigate("/", { replace: true })}
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  const { title, version, effective_date, sections } = notice;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-amber-50 flex flex-col">
      <Helmet>
        <title>
          Privacy & Consent | {title ?? framework.toUpperCase()}
        </title>
      </Helmet>

      {/* Status Card */}
      <div className="bg-white p-4 shadow mb-4 max-w-2xl mx-auto rounded">
        <div className="font-semibold">Status: Awaiting Acceptance</div>
      </div>

      {/* Header */}
      <div className="bg-orange-600 text-white py-8 px-4 text-center">
        <h1 className="text-3xl font-bold mb-2">{title ?? "Privacy & Data Use Consent"}</h1>
        <p className="text-sm">
          Version {version} | Effective {effective_date}
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-grow max-w-4xl mx-auto p-6">
        {/* Sections */}
        {(sections ?? []).map((section) => {
          const IconComponent = iconMap[section.icon?.toLowerCase() || "info"] || FileText;
          return (
            <div key={section.id} className="mb-4 border-l-4 border-gray-300 pl-4">
              <div className="flex items-center mb-2">
                <IconComponent className="mr-2" size={20} />
                <h3 className="font-semibold text-gray-900">{section.heading}</h3>
              </div>
              <p className="text-gray-700">{section.content}</p>
            </div>
          );
        })}

        {/* User Acknowledgments */}
        <div className="mb-8 bg-white p-6 rounded-xl shadow-md border-l-4 border-green-600">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-gray-900">
            <Shield className="text-green-600" size={28} />
            Acknowledgments
          </h2>
          {/* Accessibility improvements */}
          <div className="space-y-4">
            <div>
              <input
                type="checkbox"
                id="ack-read"
                checked={acceptance.acknowledgment1}
                onChange={() => handleCheckboxChange("acknowledgment1")}
                disabled={loadingState !== "idle"}
                className="mr-2"
              />
              <label htmlFor="ack-read" className="cursor-pointer">
                ✓ I have read the Privacy & Assessment Notice.
              </label>
            </div>
            <div>
              <input
                type="checkbox"
                id="ack-store"
                checked={acceptance.acknowledgment2}
                onChange={() => handleCheckboxChange("acknowledgment2")}
                disabled={loadingState !== "idle"}
                className="mr-2"
              />
              <label htmlFor="ack-store" className="cursor-pointer">
                ✓ I understand that my responses and evidence will be stored securely.
              </label>
            </div>
            <div>
              <input
                type="checkbox"
                id="ack-record"
                checked={acceptance.acknowledgment3}
                onChange={() => handleCheckboxChange("acknowledgment3")}
                disabled={loadingState !== "idle"}
                className="mr-2"
              />
              <label htmlFor="ack-record" className="cursor-pointer">
                ✓ I understand submitted assessments become part of the record.
              </label>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-col md:flex-row md:justify-center gap-4 mb-8">
          <button
            disabled={
              !(
                acceptance.acknowledgment1 &&
                acceptance.acknowledgment2 &&
                acceptance.acknowledgment3
              ) || loadingState !== "idle"
            }
            onClick={handleAccept}
            className={`px-6 py-4 rounded-lg font-semibold shadow-lg transition ${
              acceptance.acknowledgment1 &&
              acceptance.acknowledgment2 &&
              acceptance.acknowledgment3 &&
              loadingState !== "accepting"
                ? "bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            {loadingState === "accepting" ? (
              <div className="loader border-4 border-white border-t-transparent rounded-full h-6 w-6 mx-auto animate-spin"></div>
            ) : (
              <>
                <CheckCircle2 className="mr-2 inline-block" size={20} />
                I Accept & Continue
              </>
            )}
          </button>
          <button
            onClick={handleCancel}
            className="px-6 py-4 bg-gray-300 rounded-lg font-semibold shadow-lg hover:bg-gray-400 transition"
            disabled={loadingState === "accepting"}
          >
            <X size={20} className="inline-block mr-2" />
            Return Home
          </button>
        </div>

        {/* Footer note */}
        <div className="text-center text-gray-600 text-sm border-t border-gray-200 pt-4">
          <p>
            If you do not accept, you will need to read and accept the notice before starting your assessment.
          </p>
        </div>
      </div>

      {/* Cancel modal */}
      {showCancelModal && (
        <Dialog
          open={showCancelModal}
          onClose={handleCloseModal}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <Dialog.Panel className="bg-white p-6 rounded-lg max-w-sm w-full shadow-lg">
            <Dialog.Title className="text-lg font-semibold mb-4">Leave Privacy & Assessment?</Dialog.Title>
            <p className="mb-4">
              You have not accepted the Privacy & Assessment Notice. If you leave now, you will need to read and accept it before starting your assessment.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
              >
                Stay
              </button>
              <button
                onClick={handleConfirmCancel}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
              >
                Return Home
              </button>
            </div>
          </Dialog.Panel>
        </Dialog>
      )}
    </div>
  );
};

export default PrivacyAssessmentNotice;